import {
  Component, EventEmitter, Input,
  OnDestroy,
  OnInit, Output
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {Router} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, ReplaySubject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import moment from 'moment';

import {CommissionTableService} from '../../../../shared/services/commission-table.service';
import {CommissionTable, CommissionTableListItem} from '../../../../shared/interface/commission-table';
import {FormHelperService} from '../../../../shared/services/form-helper.service';

@Component({
  selector: 'app-add-commission-table',
  templateUrl: './add-commission-table.component.html',
  styleUrls: ['./add-commission-table.component.scss']
})
export class AddCommissionTableComponent implements OnInit, OnDestroy {
  @Input() commissionTable?: Observable<CommissionTable>;
  @Output() closeModification = new EventEmitter<void>();

  commissionTableObject?: CommissionTable;

  commissionTableForm: FormGroup;

  nameControl: FormControl;
  shortNameControl: FormControl;
  validFromControl: FormControl;
  validToControl: FormControl;
  sourceTableControl: FormControl;

  sourceTables: CommissionTableListItem[] = [];

  processingRequest = false;
  errors: any = {};

  todayDate: string;

  private destroyed$: ReplaySubject<boolean>
    = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private commissionTableService: CommissionTableService,
    private formHelper: FormHelperService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {
    this.commissionTableForm = this.fb.group({
      name: this.nameControl = this.fb.control('', [Validators.required]),
      shortName: this.shortNameControl = this.fb.control('', [Validators.required]),
      validFrom: this.validFromControl = this.fb.control('', [Validators.required]),
      validTo: this.validToControl = this.fb.control(''),
      sourceTable: this.sourceTableControl = this.fb.control(''),
      assignments: this.fb.array([], [Validators.minLength(1)])
    });

    this.todayDate = moment().format('YYYY-MM-DD');
  }

  ngOnInit(): void {
    if (this.commissionTable) {

      this.commissionTable
        .pipe(
          takeUntil(this.destroyed$)
        )
        .subscribe(
          (commissionTable: CommissionTable) => {
            this.commissionTableObject = commissionTable;
            this.initialize(commissionTable);
          }
        );

    } else {

      this.addAssignment();

      this.commissionTableService
        .getOptions()
        .pipe(first())
        .subscribe(
          (tables: CommissionTableListItem[]) => {
            this.sourceTables = tables;
          }
        );

      this.sourceTableControl.valueChanges
        .pipe(
          takeUntil(this.destroyed$)
        )
        .subscribe(
          (commissionTableUuid: string) => {
            this.sourceTableChanged(commissionTableUuid);
          }
        );
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  get assignments(): FormArray {
    return this.commissionTableForm
      .get('assignments') as FormArray;
  }

  addAssignment(): void {
    this.assignments.push(
      this.fb.group({
        company: this.fb.control('', [Validators.required]),
        contractType: this.fb.control('', [Validators.required])
      })
    );
  }

  removeAssignment(index: number): void {
    if (this.assignments.length <= 1) {
      return;
    }

    this.assignments.removeAt(index);
  }

  save(): void {
    if (!this.commissionTableForm.valid) {
      this.commissionTableForm.markAllAsTouched();

      this.snackBar.open(
        'Wypełnij wszystkie wymagane pola',
        '',
        {
          duration: 2000
        }
      );

      return;
    }

    if (this.hasDuplicateAssignments()) {
      this.snackBar.open(
        'Ta sama firma i rodzaj umowy zostały dodane więcej niż raz',
        '',
        {
          duration: 4000
        }
      );

      return;
    }

    this.processingRequest = true;

    this.formHelper.removeBackendErrorState(
      this.commissionTableForm,
      this.errors
    );

    this.errors = {};

    const request$ = this.commissionTableObject
      ? this.commissionTableService.update(this.commissionTableObject.uuid, this.commissionTableForm.value)
      : this.commissionTableService.create(this.commissionTableForm.value);

    request$
      .pipe(first())
      .subscribe(
        (response: any) => {

          this.processingRequest = false;

          this.snackBar.open(
            this.commissionTableObject
              ? 'Pomyślnie zaktualizowano tabelę prowizyjną'
              : 'Pomyślnie utworzono tabelę prowizyjną',
            '',
            {
              duration: 2000
            }
          );

          if (this.commissionTableObject) {
            this.closeModification.emit();
          } else {
            this.router.navigate([
              '/tabele-prowizyjne/szczegoly',
              response.uuid
            ]);
          }
        },
        (error: HttpErrorResponse) => {

          this.processingRequest = false;

          this.snackBar.open(
            'Podczas zapisywania tabeli prowizyjnej wystąpił błąd',
            error.status.toString(),
            {
              duration: 3000
            }
          );

          if (error.status === 400) {

            this.errors =
              error.error.error.list;

            this.formHelper
              .setBackendErrorState(
                this.commissionTableForm,
                this.errors
              );
          }
        }
      );
  }

  cancel(): void {
    if (this.commissionTableObject) {
      this.closeModification.emit();

      return;
    }

    this.router.navigate(['/tabele-prowizyjne']);
  }

  hasDuplicateAssignments(): boolean {
    const values = this.assignments.getRawValue();

    const keys = values
      .filter(item => item.company && item.contractType)
      .map(item => `${item.company}:${item.contractType}`);

    return new Set(keys).size !== keys.length;
  }

  sourceTableChanged(commissionTableUuid: string): void {
    if (!commissionTableUuid) {
      return;
    }

    const sourceTable = this.sourceTables.find(
      (table: CommissionTableListItem) =>
        table.uuid === commissionTableUuid
    );

    if (!sourceTable?.validTo) {
      return;
    }

    const nextValidFrom = moment(sourceTable.validTo)
      .add(1, 'day')
      .format('YYYY-MM-DD');

    this.validFromControl.setValue(nextValidFrom);
  }

  initialize(
    commissionTable: CommissionTable
  ): void {

    this.assignments.clear();

    this.commissionTableForm.reset({
      name: commissionTable.name,
      shortName: commissionTable.shortName,
      validFrom: commissionTable.validFrom,
      validTo: commissionTable.validTo || '',
      sourceTable: commissionTable.sourceTable?.uuid ?? null,
    });

    commissionTable.assignments
      .forEach(assignment => {

        this.assignments.push(
          this.fb.group({
            company: this.fb.control(assignment.company.id, [Validators.required]),
            contractType: this.fb.control(assignment.contractType.id, [Validators.required])
          })
        );

      });

    if (this.assignments.length === 0) {
      this.addAssignment();
    }

    this.sourceTableControl.disable({
      emitEvent: false
    });
  }
}
